import React, { useState, useEffect, useRef } from 'react';
import SubscriptionRequestsService from '../../services/SubscriptionRequestsService';
import {
	Button,
	ButtonSizeType,
	ButtonGroup,
	Icon,
	TextPassage,
	Table,
	Toolbar,
	ToolbarItem,
	Select,
	Section,
} from '../common';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import './PolicyExemptionRequestSelectRG.css';
import AddResourceGroupModal, { ResourceGroupEntry, EXPIRATION_OPTIONS } from './components/AddResourceGroupModal';
import { AzureResourceModel } from '../../models/viewModels/Subscriptions';

export interface PolicyRequestSelectedRGProps {
	selectedSubscriptions: SubscriptionRequest[];
	initialEntriesBySubscription?: Record<number, ResourceGroupEntry[]>;
	initialBulkExpirationBySubscription?: Record<number, string>;
	initialRGsBySubscription?: Record<number, AzureResourceModel[]>;
	initialResourcesByRG?: Record<string, AzureResourceModel[]>;
	onNext: (
		entriesBySubscription: Record<number, ResourceGroupEntry[]>,
		bulkExpirationBySubscription: Record<number, string>,
		rgsBySubscription: Record<number, AzureResourceModel[]>,
		resourcesByRG: Record<string, AzureResourceModel[]>
	) => void;
	onBack: () => void;
}

const PolicyExemptionRequestSelectRG = ({
	selectedSubscriptions,
	initialEntriesBySubscription,
	initialBulkExpirationBySubscription,
	initialRGsBySubscription,
	initialResourcesByRG,
	onNext,
	onBack,
}: PolicyRequestSelectedRGProps) => {
	const [modalSubscriptionId, setModalSubscriptionId] = useState<number | null>(null);
	const [entriesBySubscription, setEntriesBySubscription] = useState<Record<number, ResourceGroupEntry[]>>(
		() => initialEntriesBySubscription ?? {}
	);
	const [bulkExpirationBySubscription, setBulkExpirationBySubscription] = useState<Record<number, string>>(
		() => initialBulkExpirationBySubscription ?? {}
	);
	const [rgsBySubscription, setRGsBySubscription] = useState<Record<number, AzureResourceModel[]>>(
		() => initialRGsBySubscription ?? {}
	);
	const [resourcesByRG, setResourcesByRG] = useState<Record<string, AzureResourceModel[]>>(
		() => initialResourcesByRG ?? {}
	);
	const subscriptionRequestsService = useRef(new SubscriptionRequestsService());

	// Prefetch RGs for all subscriptions as soon as the step mounts
	useEffect(() => {
		const prefetchRGs = async () => {
			for (const sub of selectedSubscriptions) {
				if (rgsBySubscription[sub.id]) continue;
				try {
					const rgs = await subscriptionRequestsService.current.getAllResourceGroupsInSubscription(sub.id);
					setRGsBySubscription((prev) => ({ ...prev, [sub.id]: rgs }));
				} catch (error) {
					console.error(`Failed to prefetch RGs for subscription ${sub.id}:`, error);
				}
			}
		};
		prefetchRGs();
	}, [selectedSubscriptions]);

	const handleBack = () => {
		onBack();
	};

	const handleNext = () => {
		onNext(entriesBySubscription, bulkExpirationBySubscription, rgsBySubscription, resourcesByRG);
	};

	const handleAddResourceGroup = async (subscriptionId: number) => {
		setModalSubscriptionId(subscriptionId);
	};

	const handleModalConfirm = (entry: ResourceGroupEntry) => {
		const subId = modalSubscriptionId!;
		setEntriesBySubscription((prev) => ({
			...prev,
			[subId]: [...(prev[subId] ?? []), entry],
		}));
		setBulkExpirationBySubscription((prev) => {
			if (prev[subId]) return prev;
			const matchedOption = EXPIRATION_OPTIONS.find((o) => o.text === entry.expiration);
			return { ...prev, [subId]: matchedOption?.value ?? entry.expiration };
		});
		setModalSubscriptionId(null);
	};

	const handleModalCancel = () => {
		setModalSubscriptionId(null);
	};

	const handleBulkExpirationChange = (subscriptionId: number, value: string) => {
		setBulkExpirationBySubscription((prev) => ({ ...prev, [subscriptionId]: value }));
		const label = EXPIRATION_OPTIONS.find((o) => o.value === value)?.text ?? value;
		setEntriesBySubscription((prev) => ({
			...prev,
			[subscriptionId]: (prev[subscriptionId] ?? []).map((entry) => ({
				...entry,
				expiration: label,
			})),
		}));
	};

	const handleRemoveEntry = (subscriptionId: number, entryIndex: number) => {
		setEntriesBySubscription((prev) => ({
			...prev,
			[subscriptionId]: (prev[subscriptionId] ?? []).filter((_, i) => i !== entryIndex),
		}));
	};

	const isButtonEnabled = Object.values(bulkExpirationBySubscription).length == selectedSubscriptions.length;

	return (
		<>
			<TextPassage>
				<p>
					Review the selected subscriptions and choose the Resource Groups and Resources for which you want to
					apply the policy exemption.
				</p>
			</TextPassage>

			{selectedSubscriptions.length === 0 ? (
				<TextPassage>
					<p>No subscriptions selected. Please go back and select at least one subscription.</p>
				</TextPassage>
			) : (
				<>
					{selectedSubscriptions.map((sub, index) => (
						<Section
							key={sub.id}
							title={
								sub.createdSubscriptionName ?? sub.createdSubscriptionId ?? `Subscription ${index + 1}`
							}
							type="em-c-section--expandable"
							isInError={!bulkExpirationBySubscription[sub.id]}
							titleFontSize="large"
							marginNone
						>
							<Table
								header={
									<Toolbar>
										<ToolbarItem>
											<ButtonGroup>
												<p className="policyRequestRG-expirationText"> Expiration: </p>
												<Select
													value={bulkExpirationBySubscription[sub.id] ?? ''}
													onChange={(e) => handleBulkExpirationChange(sub.id, e.target.value)}
													options={[
														{ value: '', text: 'Apply to all...' },
														...EXPIRATION_OPTIONS,
													]}
													note="The expiration day selected would be applied to the entire subscription"
													validation={{
														required: true,
														rules: [
															{
																assert: (val) =>
																	val === '' ||
																	EXPIRATION_OPTIONS.some((o) => o.value === val),
																message:
																	'The expiration day selected would be applied to the entire subscription',
															},
														],
													}}
												/>
											</ButtonGroup>
										</ToolbarItem>
										<ToolbarItem right>
											<Button
												id={`Add-${sub.id}`}
												name="Add"
												size="small"
												variant="primary"
												onClick={() => handleAddResourceGroup(sub.id)}
											>
												<Icon icon="plus" size="small" />
												Add
											</Button>
										</ToolbarItem>
									</Toolbar>
								}
							>
								<Table.Head>
									<Table.Head.Row>
										<Table.Head.Cell>Resource Group</Table.Head.Cell>
										<Table.Head.Cell>Resource</Table.Head.Cell>
										<Table.Head.Cell>Expiration</Table.Head.Cell>
										<Table.Head.Cell></Table.Head.Cell>
									</Table.Head.Row>
								</Table.Head>
								<Table.Body>
									{(entriesBySubscription[sub.id] ?? []).length === 0 ? (
										<Table.Row>
											<Table.Cell colSpan={4}>
												If no RG / Resources are selected, the policies will be applied at a
												subscription level
											</Table.Cell>
										</Table.Row>
									) : (
										(entriesBySubscription[sub.id] ?? []).map((entry, i) => (
											<Table.Row key={i}>
												<Table.Cell>{entry.resourceGroupName}</Table.Cell>
												<Table.Cell>{entry.resourceName}</Table.Cell>
												<Table.Cell>{entry.expiration}</Table.Cell>
												<Table.Cell>
													<Button
														variant="bare"
														size="small"
														onClick={() => handleRemoveEntry(sub.id, i)}
													>
														<Icon icon="trashcan" size="small" />
													</Button>
												</Table.Cell>
											</Table.Row>
										))
									)}
								</Table.Body>
							</Table>
						</Section>
					))}
				</>
			)}
			<AddResourceGroupModal
				visible={modalSubscriptionId !== null}
				subscriptionId={modalSubscriptionId}
				preselectedExpiration={
					modalSubscriptionId !== null ? bulkExpirationBySubscription[modalSubscriptionId] : undefined
				}
				cachedRGs={modalSubscriptionId !== null ? rgsBySubscription[modalSubscriptionId] : undefined}
				cachedResources={resourcesByRG}
				existingEntries={modalSubscriptionId !== null ? (entriesBySubscription[modalSubscriptionId] ?? []) : []}
				onRGsLoaded={(rgs) =>
					modalSubscriptionId !== null &&
					setRGsBySubscription((prev) => ({ ...prev, [modalSubscriptionId]: rgs }))
				}
				onResourcesLoaded={(rgId, res) => setResourcesByRG((prev) => ({ ...prev, [rgId]: res }))}
				onConfirm={handleModalConfirm}
				onCancel={handleModalCancel}
			/>
			<br />
			<ButtonGroup>
				<Button variant="secondary" size="small" onClick={handleBack}>
					<Icon icon="chevron-left" size="small" />
					<span>Back</span>
				</Button>
				<Button variant="primary" size="small" disabled={!isButtonEnabled} onClick={handleNext}>
					<span>Next Step</span>
					<Icon icon="chevron-right" size="small" />
				</Button>
			</ButtonGroup>
		</>
	);
};

export default PolicyExemptionRequestSelectRG;
